import { callDataSourceNames } from '@ngsos/portal-core/portal';
import type { CallsSnapshot, IncidentsSnapshot } from '@ngsos/portal-core/portal';

import { DevJsonDetails } from './DevJsonDetails';

export type DevPortalStoresPanelProps = {
  incidents: IncidentsSnapshot;
  calls: CallsSnapshot;
};

function shortId(id: string | null | undefined): string {
  return id == null || id === '' ? '—' : id.slice(0, 8);
}

function timeOf(iso: string | null | undefined): string {
  return iso == null || iso === '' ? '—' : iso.slice(11, 19);
}

export function DevPortalStoresPanel({ incidents, calls }: DevPortalStoresPanelProps) {
  const knownIncidents = [...incidents.incidentsById.values()];
  const liveIds = new Set(incidents.liveIncidents.map((incident) => incident.id));

  return (
    <section className="d-flex flex-column gap-4">
      <div>
        <h2 className="h5">
          IncidentStore <span className="badge text-bg-secondary align-middle">{incidents.dataState}</span>
        </h2>
        <div className="small text-body-secondary mb-2">
          {incidents.liveIncidents.length} incident(s) in the live window, {knownIncidents.length} in the id index
        </div>

        {knownIncidents.length === 0 ? (
          <div className="small text-body-secondary">The store holds no incidents.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover small font-monospace align-middle">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Live</th>
                  <th>State</th>
                  <th>Created</th>
                  <th>Closed</th>
                  <th>Title</th>
                  <th>Chat</th>
                  <th>Video</th>
                  <th>Agencies</th>
                  <th>Positions</th>
                  <th>Attachments</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {knownIncidents.map((incident) => (
                  <tr key={incident.id}>
                    <td title={incident.id}>{shortId(incident.id)}</td>
                    <td>{liveIds.has(incident.id) ? 'yes' : 'no'}</td>
                    <td>{incident.state}</td>
                    <td title={incident.createdTimeUtc}>{timeOf(incident.createdTimeUtc)}</td>
                    <td title={incident.closedTimeUtc ?? ''}>{timeOf(incident.closedTimeUtc)}</td>
                    <td className="text-break">{incident.title}</td>
                    <td title={incident.chatId ?? ''}>{shortId(incident.chatId)}</td>
                    <td>{incident.videoState}</td>
                    <td>{incident.involvedAgencies.length}</td>
                    <td>{incident.positions.length}</td>
                    <td>{incident.attachments.length}</td>
                    <td>
                      <DevJsonDetails summary="json" value={incident} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="h5">CallStore</h2>
        <div className="small text-body-secondary mb-2">{calls.calls.length} call(s)</div>

        {calls.calls.length === 0 ? (
          <div className="small text-body-secondary">The store holds no calls.</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm table-hover small font-monospace align-middle">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>Call id</th>
                  <th>Source</th>
                  <th>Start</th>
                  <th>End</th>
                    <th>Realtime revision</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {calls.calls.map((call) => (
                  <tr key={call.id}>
                    <td title={call.id}>{shortId(call.id)}</td>
                    <td>{call.callId}</td>
                    <td>{callDataSourceNames(call.sourceKinds).join(', ')}</td>
                    <td title={call.startTimeUtc}>{timeOf(call.startTimeUtc)}</td>
                    <td title={call.endTimeUtc ?? ''}>{timeOf(call.endTimeUtc)}</td>
                    <td>{call.realtimeRevision}</td>
                    <td>
                      <DevJsonDetails summary="json" value={call} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
